import React, { useState, useEffect } from 'react';
import { Mail, Phone, Calendar, Eye } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactsManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, [filter]);

  const fetchContacts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const url = filter === 'all' 
        ? `${BACKEND_URL}/api/contact`
        : `${BACKEND_URL}/api/contact?status_filter=${filter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(
        `${BACKEND_URL}/api/contact/${id}/status?new_status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      contacted: 'bg-blue-100 text-blue-700',
      resolved: 'bg-green-100 text-green-700'
    };
    return colors[status] || colors.pending;
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-black mb-4">Contact Inquiries</h1>
        <div className="flex gap-2">
          {['all', 'pending', 'contacted', 'resolved'].map(status => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              onClick={() => setFilter(status)}
              className={filter === status ? 'bg-yellow-500 text-black' : ''}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {contacts.map(contact => (
          <Card key={contact.id} className="border-2 border-zinc-200">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-bold text-lg text-black">{contact.name}</h3>
                  <p className="text-zinc-600">{contact.subject}</p>
                </div>
                <Badge className={getStatusColor(contact.status)}>
                  {contact.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div className="flex items-center gap-2 text-zinc-600">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${contact.email}`} className="hover:text-yellow-600">
                    {contact.email}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-zinc-600">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${contact.phone}`} className="hover:text-yellow-600">
                    {contact.phone}
                  </a>
                </div>
              </div>

              <p className="text-zinc-700 mb-4 p-3 bg-zinc-50 rounded-lg">
                {contact.message}
              </p>

              <div className="flex gap-2">
                {contact.status !== 'contacted' && (
                  <Button
                    size="sm"
                    onClick={() => updateStatus(contact.id, 'contacted')}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    Mark Contacted
                  </Button>
                )}
                {contact.status !== 'resolved' && (
                  <Button
                    size="sm"
                    onClick={() => updateStatus(contact.id, 'resolved')}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    Mark Resolved
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {contacts.length === 0 && (
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-12 text-center">
            <Mail className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No Contacts Found</h3>
            <p className="text-zinc-600">No contact inquiries match your filter</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContactsManagement;
